import classNames from 'classnames';

import { useRouteContext } from 'src/hooks/useRouteContext';

import { ComponentProps } from 'src/types/component-props';
import { CoreGlobalNavigationHeaderData } from 'src/types/components';

import Image from 'components/BaseHelpers/Image';
import Link from 'components/BaseHelpers/Link';
import AncillaryNavigation from 'components/Core/CoreNavigationHeader/components/AncillaryNavigation';
import OtherCollegeNavigation from 'components/Core/CoreNavigationHeader/components/OtherCollegeNavigation';

import DesktopPrimaryNav from './components/DesktopPrimaryNav';
import MobilePrimaryNav from './components/MobilePrimaryNav';
import NavSearch from './components/NavSearch';

import styles from './CoreNavigationHeader.module.scss';

export type CoreNavigationHeaderProps = ComponentProps & CoreGlobalNavigationHeaderData;

const NavLogo = ({ logo }: { logo: CoreNavigationHeaderProps['fields']['logo'] }) => {
  const { routeData } = useRouteContext();

  const { image, alt, url } = logo;
  if (!image) {
    return <></>;
  }

  const isHomepage = routeData?.route.path === '/';

  if (isHomepage) {
    return (
      <h1 aria-label="St Lukes College of Health Sciences" className={styles['img-container']}>
        <Image fill={true} {...image} />
      </h1>
    );
  }

  const linkIsHomepage = url.href === '/';
  let ariaLabel = 'Go to the St Lukes homepage';
  let href = '/';

  if (!linkIsHomepage) {
    // Either use Alt text, or derive from target URL page's title
    ariaLabel = alt !== '' ? alt : `Go to the ${url.label} homepage`;
    href = url.href;
  }
  return (
    <Link href={href} aria-label={ariaLabel} className={styles['img-container']}>
      <Image fill={true} {...image} />
    </Link>
  );
};

const CoreNavigationHeader = ({ fields }: CoreNavigationHeaderProps): JSX.Element => {
  const {
    primaryNavigationItems,
    ancillaryNavigation,
    logo,
    otherCollegeNavigation,
    searchPageBaseURL,
    searchInputLabel = 'Search',
    termInputPlaceholderLabel = 'What can we help you find?',
  } = fields;
  return (
    <header className={styles.main}>
      <a href="#main" className={styles['skip-link']}>
        {'Skip to main content'}
      </a>
      <div className={styles.topNavBarWrapper}>
        <div className={classNames(styles.topNavBar, 'global__wrapper--1920')}>
          {otherCollegeNavigation && (
            <OtherCollegeNavigation
              className={styles.otherCollegeNavigation}
              {...otherCollegeNavigation}
            />
          )}
          <AncillaryNavigation className={styles.ancillaryNavigation} {...ancillaryNavigation} />
        </div>
      </div>
      <div className={styles.mainNavBarWrapper}>
        <div className={classNames(styles.mainNavBar, 'global__wrapper--1920')}>
          <NavLogo logo={logo} />
          <div className={styles.menuWrapper}>
            <DesktopPrimaryNav navigationItems={primaryNavigationItems} />
            {searchPageBaseURL && (
              <NavSearch
                searchTarget={searchPageBaseURL}
                searchInputLabel={searchInputLabel}
                termInputPlaceholderLabel={termInputPlaceholderLabel}
              />
            )}
            <MobilePrimaryNav
              ancillaryNavigationItems={ancillaryNavigation}
              primaryNavigationItems={primaryNavigationItems}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default CoreNavigationHeader;
